/**
 * Page « SAV » — tickets Gorgias.
 *
 * Indicateurs, courbes et répartitions viennent de la base (`gorgias_tickets`,
 * sans client ni contenu). Le tableau des derniers messages est lu EN DIRECT
 * chez Gorgias à l'affichage : rien n'en est stocké, ni en base ni dans le
 * navigateur au-delà de la visite.
 */

import type { SavMessage, SavTotals } from '@shared/types/sav';

import { Card } from '../components/Card';
import { DonutChart, type DonutSlice } from '../components/DonutChart';
import { SavTicketsChart } from '../components/SavTicketsChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile, type StatDelta } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { useSavMessages, useSavStats } from '../hooks/useSav';
import { volumeDelta } from '../lib/deltas';
import { formatDayShort, formatDecimal, formatInstant, formatInteger } from '../lib/format';
import { CATEGORICAL_COLORS, OTHER_COLOR } from '../lib/theme';

const TOP_SLICES = 9;
const days = (value: number) => `${formatDecimal(value)} j`;

/** Les neuf premières parts, puis « Autres ». */
function topSlices(rows: ReadonlyArray<{ label: string; value: number }>): DonutSlice[] {
  const slices: DonutSlice[] = rows.slice(0, TOP_SLICES).map((row, index) => ({
    key: row.label,
    label: row.label,
    value: row.value,
    color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
  }));
  const others = rows.slice(TOP_SLICES).reduce((sum, row) => sum + row.value, 0);
  if (others > 0) slices.push({ key: 'others', label: 'Autres', value: others, color: OTHER_COLOR });
  return slices;
}

const STATUS_LABELS: Record<string, string> = { open: 'ouvert', closed: 'fermé' };

function MessagesCard({ token, onUnauthorized }: { token: string | null; onUnauthorized: () => void }) {
  const query = useSavMessages(token, onUnauthorized);
  const messages: SavMessage[] = query.data?.pages.flatMap((page) => page.messages) ?? [];

  return (
    <Card
      title="Derniers messages"
      subtitle="Lus en direct chez Gorgias, les plus récents d'abord — rien n'est stocké dans le dashboard"
      className="chart-grid__wide"
    >
      {query.isPending ? (
        <p className="empty">Chargement des messages…</p>
      ) : query.error ? (
        <p className="notice notice--error" role="alert">
          {query.error.message}
        </p>
      ) : messages.length === 0 ? (
        <p className="empty">Aucun message.</p>
      ) : (
        <>
          {/* `data--stack` : sur téléphone, chaque message devient une fiche
              (date · expéditeur · canal · statut, sujet, corps) au lieu d'une
              ligne de six colonnes à faire défiler de côté. */}
          <div className="table-wrap">
            <table className="data data--stack">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>De</th>
                  <th>Canal</th>
                  <th>Statut</th>
                  <th>Sujet</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => (
                  <tr key={message.id}>
                    <td>{formatInstant(message.createdAt)}</td>
                    <td className="wrap">{message.fromAgent ? <em>agent</em> : message.senderEmail ?? '—'}</td>
                    <td>{message.channel}</td>
                    <td>{message.ticketStatus ? STATUS_LABELS[message.ticketStatus] ?? message.ticketStatus : '—'}</td>
                    <td className="wrap">
                      <a href={message.ticketUrl} target="_blank" rel="noreferrer noopener">
                        {message.subject || `Ticket ${message.ticketId}`}
                      </a>
                    </td>
                    <td className="wrap">
                      <div className="message-body">{message.body || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.hasNextPage && (
            <button type="button" className="btn btn--small" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              {query.isFetchingNextPage ? 'Chargement…' : 'Messages plus anciens'}
            </button>
          )}
        </>
      )}
    </Card>
  );
}

export function SavPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const query = useSavStats({ from: period.from, to: period.to, token, onUnauthorized: signOut });

  const stats = query.data;
  const totals = stats?.comparison.current.totals;
  const overlap = stats?.comparison.comparisonsOverlap ?? false;
  const refs = overlap
    ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
    : [
        { label: 'période préc.', key: 'previous' as const },
        { label: "l'an dernier", key: 'yearAgo' as const },
      ];
  const deltas = (pick: (t: SavTotals) => number | null, format: (value: number) => string, higherIsBetter: boolean | null): StatDelta[] =>
    refs.map(({ label, key }) => {
      const ref = stats?.comparison[key].totals;
      return volumeDelta({ label, available: (ref?.opened ?? 0) + (ref?.closed ?? 0) > 0 }, totals ? pick(totals) : null, ref ? pick(ref) : null, format, higherIsBetter, 'aucun ticket');
    });

  const channelSlices = topSlices((stats?.byChannel ?? []).map((row) => ({ label: row.channel, value: row.tickets })));
  const tagSlices = topSlices((stats?.byTag ?? []).map((row) => ({ label: row.tag ?? 'Sans tag', value: row.tickets })));
  const dailyDesc = [...(stats?.daily ?? [])].reverse();

  return (
    <SectionShell
      lastSyncAt={stats?.meta.lastSyncAt}
      isPending={query.isPending}
      isFetching={query.isFetching}
      error={query.error}
      hasData={Boolean(stats)}
      title={
        <h1 className="page-title">
          SAV
          <span className="page-title__sub">Tickets Gorgias : volumes, délais de fermeture, canaux et motifs</span>
        </h1>
      }
    >
      {stats && !stats.meta.lastSyncAt && (
        <p className="notice">
          Aucune synchronisation Gorgias n'a encore abouti. Vérifier que le conteneur <code>sync_gorgias</code> tourne
          (<code>docker compose logs sync_gorgias</code>).
        </p>
      )}
      {stats?.meta.lastSyncAt && !stats.meta.historyImported && (
        <p className="notice">Import de l'historique Gorgias en cours : chiffres incomplets.</p>
      )}
      {stats?.meta.lastSyncFailed && (
        <p className="notice notice--error" role="alert">
          Le dernier passage du sync Gorgias a échoué. Les chiffres sont ceux du {formatInstant(stats.meta.lastSyncAt)}.
        </p>
      )}

      <h2 className="section-heading">SAV KPIs</h2>
      <div className="kpi-row">
        <StatTile
          label="Tickets ouverts"
          value={formatInteger(totals?.opened ?? 0)}
          deltas={deltas((t) => t.opened, formatInteger, null)}
          hint="créés sur la période, hors spam et corbeille"
        />
        <StatTile
          label="Encore ouverts"
          value={formatInteger(totals?.stillOpen ?? 0)}
          deltas={deltas((t) => t.stillOpen, formatInteger, false)}
          hint="créés sur la période et pas encore fermés aujourd'hui"
        />
        <StatTile
          label="Tickets fermés"
          value={formatInteger(totals?.closed ?? 0)}
          deltas={deltas((t) => t.closed, formatInteger, true)}
          hint="fermés sur la période, quelle que soit leur date de création"
        />
        <StatTile
          label="Temps moyen fermeture"
          value={totals?.avgCloseDays != null ? days(totals.avgCloseDays) : '—'}
          deltas={deltas((t) => t.avgCloseDays, days, false)}
          hint="création → fermeture, des tickets créés sur la période et déjà fermés"
        />
        <StatTile
          label="Total tickets ouverts"
          value={formatInteger(stats?.openNow ?? 0)}
          hint={`à l'instant, toutes dates confondues — au dernier sync (${formatInstant(stats?.meta.lastSyncAt ?? null)})`}
        />
      </div>

      <div className="chart-grid">
        <SavTicketsChart daily={stats?.daily ?? []} />

        <DonutChart title="Par canal" subtitle="Tickets créés sur la période" slices={channelSlices} formatValue={formatInteger} emptyMessage="Aucun ticket sur cette période." />
        <DonutChart
          title="Par tag"
          subtitle="Tickets créés sur la période — un ticket à plusieurs tags compte dans chacun"
          slices={tagSlices}
          formatValue={formatInteger}
          emptyMessage="Aucun ticket sur cette période."
        />

        <Card title="Tickets jour par jour" subtitle="Le plus récent en haut" className="chart-grid__wide">
          <div className="table-wrap table-scroll" style={{ maxHeight: 420 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Tickets ouverts</th>
                  <th className="num">Tickets fermés</th>
                  <th className="num">Encore ouverts</th>
                  <th className="num">Temps moyen fermeture</th>
                </tr>
              </thead>
              <tbody>
                {dailyDesc.map((day) => (
                  <tr key={day.date}>
                    <td>{formatDayShort(day.date)}</td>
                    <td className="num">{formatInteger(day.opened)}</td>
                    <td className="num">{formatInteger(day.closed)}</td>
                    <td className="num">{formatInteger(day.stillOpen)}</td>
                    <td className="num">{day.avgCloseDays !== null ? days(day.avgCloseDays) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td className="num">
                      <strong>{formatInteger(totals.opened)}</strong>
                    </td>
                    <td className="num">
                      <strong>{formatInteger(totals.closed)}</strong>
                    </td>
                    <td className="num">
                      <strong>{formatInteger(totals.stillOpen)}</strong>
                    </td>
                    <td className="num">
                      <strong>{totals.avgCloseDays !== null ? days(totals.avgCloseDays) : '—'}</strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        <MessagesCard token={token} onUnauthorized={signOut} />
      </div>
    </SectionShell>
  );
}
